import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CreateSchoolModal from './CreateSchoolModal';
import { fetchProfile } from '../utils/api';
import { ADMIN_ROLE, FINANCIAL_STAFF, FINANCIAL_VIEWER_STAFF } from '../utils/roles';
import './Sidebar.css';
import './_Buttons.css';

const Sidebar = () => {
    const location = useLocation();
    const [schools, setSchools] = useState([]);
    const [showSchools, setShowSchools] = useState(false);
    const [showAcademic, setShowAcademic] = useState(false);
    const [showFinances, setShowFinances] = useState(false);
    const [isCreateSchoolModalOpen, setCreateSchoolModalOpen] = useState(false);
    const [canSeeFinances, setCanSeeFinances] = useState(false);
    const token = localStorage.getItem('token');

    useEffect(() => {
        const checkPermissions = async () => {
            try {
                const profileData = await fetchProfile();
                const userRoles = profileData.data.roles || [];
                const isAdmin = userRoles.some(role => role.id === ADMIN_ROLE);
                const isFinancialStaff = userRoles.some(role => role.id === FINANCIAL_STAFF);
                const isFinancialViewer = userRoles.some(role => role.id === FINANCIAL_VIEWER_STAFF);
                setCanSeeFinances(isAdmin || isFinancialStaff || isFinancialViewer);
            } catch (error) {
                console.error("Could not fetch profile for permissions check", error);
            }
        };

        checkPermissions();
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        window.location.href = '/login';
    };

    const isTabActive = (path) => {
        const { pathname } = location;
        if (path === '/academic') {
            const academicPaths = ['/roster', '/classes', '/terms', '/lss', '/reports/stats'];
            return academicPaths.some(p => pathname.startsWith(p));
        }
        if (path === '/finances') {
            const financePaths = ['/invoices', '/recurring-invoices', '/fees-discounts', '/expenses', '/payments', '/reports/financial'];
            return financePaths.some(p => pathname.startsWith(p));
        }
        if (path === '/schools') {
            return false;
        }
        return pathname === path;
    };

    const getLinkClass = (path) => {
        return isTabActive(path) ? 'active' : '';
    };

    const getIcon = (name) => {
        return `/icons/sidebar/${name}.svg`;
    }

    const fetchSchools = async () => {
        if (!token) {
            return;
        }

        try {
            const response = await fetch('/api/v1/schools', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                }
                throw new Error(`Failed to fetch schools. Status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success && result.data) {
                setSchools(result.data);
            } else {
                console.error(result.message || 'Failed to get schools data.');
            }
        } catch (err) {
            console.error(err.message);
        }
    };

    const handleSchoolsClick = () => {
        setShowSchools(!showSchools);
        if (!showSchools) {
            fetchSchools();
        }
    };

    const handleAcademicClick = () => {
        setShowAcademic(!showAcademic);
    };

    const handleSchoolSelect = async (school) => {
        if (school.current || !school.active) {
            return;
        }

        try {
            const response = await fetch(`/api/v1/schools/${school.id}/select`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                }
                throw new Error(`Failed to select school. Status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success && result.data.token) {
                localStorage.setItem('token', result.data.token);
                localStorage.setItem('timezone', result.data.timezone);
                window.location.href = '/dashboard';
            } else {
                console.error(result.message || 'Failed to select school.');
                alert(result.message || 'Failed to select school.');
            }
        } catch (err) {
            console.error(err.message);
            alert(err.message);
        }
    };

    const handleSchoolCreated = () => {
        fetchSchools();
    };

    return (
        <div className="sidebar">
            <CreateSchoolModal
                isOpen={isCreateSchoolModalOpen}
                onClose={() => setCreateSchoolModalOpen(false)}
                onSchoolCreated={handleSchoolCreated}
            />
            <div className="sidebar-wrapper">
                <div className="sidebar-header">
                    <img src="/logo.png" alt="aistCRM" className="logo-img" />
                </div>
                <ul className="sidebar-menu">
                    <li><Link to="/dashboard" className={getLinkClass('/dashboard')}><img src={getIcon('dashboard')} alt="dashboard" className="sidebar-icon" />Dashboard</Link></li>
                    <li onClick={handleSchoolsClick} className="sidebar-school-item">
                        <a href="#" className={getLinkClass('/schools')}>
                            <img src={getIcon('schools')} alt="schools" className="sidebar-icon" />
                            Schools
                            <span className={`sidebar-arrow ${showSchools ? 'open' : ''}`}></span>
                        </a>
                    </li>
                    {showSchools && (
                        <ul className="sidebar-submenu">
                            {schools.map(school => {
                                const schoolClasses = [];
                                if (school.current) {
                                    schoolClasses.push('sidebar-current-school');
                                }
                                if (!school.active) {
                                    schoolClasses.push('sidebar-inactive-school');
                                }

                                return (
                                    <li key={school.id} className={schoolClasses.join(' ')}>
                                        <a href="#" onClick={(e) => {
                                            e.preventDefault();
                                            handleSchoolSelect(school);
                                        }}>
                                            {school.name}
                                        </a>
                                    </li>
                                );
                            })}
                            <li className='sidebar-create-school-button'><a href="#" onClick={() => setCreateSchoolModalOpen(true)}>Create School</a></li>
                        </ul>
                    )}
                    <li><Link to="/staff" className={getLinkClass('/staff')}><img src={getIcon('staff')} alt="staff" className="sidebar-icon" />Staff</Link></li>
                    <li><Link to="/students" className={getLinkClass('/students')}><img src={getIcon('students')} alt="students" className="sidebar-icon" />Students</Link></li>
                    <li><Link to="/agents" className={getLinkClass('/agents')}><img src={getIcon('agents')} alt="agents" className="sidebar-icon" />Agents</Link></li>
                    <li onClick={handleAcademicClick} className="sidebar-school-item">
                        <a href="#" className={getLinkClass('/academic')}>
                            <img src={getIcon('academic')} alt="academic" className="sidebar-icon" />
                            Academic
                            <span className={`sidebar-arrow ${showAcademic ? 'open' : ''}`}></span>
                        </a>
                     </li>
                    {showAcademic && (
                        <ul className="sidebar-submenu">
                            <li><Link to="/roster" className={getLinkClass('/roster')}>Roster</Link></li>
                            <li><Link to="/classes" className={getLinkClass('/classes')}>Classes</Link></li>
                            <li><Link to="/terms" className={getLinkClass('/terms')}>Terms</Link></li>
                            <li><Link to="/lss" className={getLinkClass('/lss')}>Levels, Streams, Subjects</Link></li>
                            <li><Link to="/reports/stats" className={getLinkClass('/reports/stats')}>Reports</Link></li>
                        </ul>
                    )}
                    {canSeeFinances && (
                        <li onClick={() => setShowFinances(!showFinances)} className="sidebar-school-item">
                            <a href="#" className={getLinkClass('/finances')}>
                                <img src={getIcon('finances')} alt="finances" className="sidebar-icon" />
                                Finances
                                <span className={`sidebar-arrow ${showFinances ? 'open' : ''}`}></span>
                            </a>
                        </li>
                    )}
                    {canSeeFinances && showFinances && (
                        <ul className="sidebar-submenu">
                            <li><Link to="/invoices" className={getLinkClass('/invoices')}>Invoices</Link></li>
                            <li><Link to="/recurring-invoices" className={getLinkClass('/recurring-invoices')}>Recurring Invoices</Link></li>
                            <li><Link to="/fees-discounts" className={getLinkClass('/fees-discounts')}>Fees & Discounts</Link></li>
                            <li><Link to="/expenses" className={getLinkClass('/expenses')}>Expenses</Link></li>
                            <li><Link to="/payments" className={getLinkClass('/payments')}>Payments</Link></li>
                            <li><Link to="/reports/financial" className={getLinkClass('/reports/financial')}>Reports</Link></li>
                           </ul>
                          )}
                    <li><Link to="/policies" className={getLinkClass('/policies')}><img src={getIcon('policies')} alt="policies" className="sidebar-icon" />Policies</Link></li>
                </ul>
            </div>
            <button onClick={handleLogout} className="logout-button text-button">Logout</button>
        </div>
    );
};

export default Sidebar;
